import React from 'react';
import { createPortal } from 'react-dom';
import { format, formatDistanceToNow } from 'date-fns';
import Button from './Button';

/**
 * Offered on encoding-screen load when a local autosave draft exists for this
 * exact client + user + screen + fiscal year.
 *
 * Blocking on purpose: the grid underneath already shows empty starting rows,
 * so a non-blocking banner would let someone type a line and then have Restore
 * overwrite it. The timestamp is shown both absolutely and relatively — "2:31
 * PM" tells them which sitting it was, "about 3 hours ago" tells them whether
 * it is still worth anything.
 *
 * Portaled to document.body for the same reason as ConfirmModal: `fixed` only
 * reliably covers the viewport when it isn't nested inside a scroll container
 * or padded page shell.
 */
const RestoreDraftModal = ({ draft, onRestore, onDiscard, screenLabel = 'this screen' }) => {
  if (!draft) return null;

  const savedAt = new Date(draft.savedAt);
  const rowCount = draft.filledCount ?? draft.rows?.length ?? 0;

  return createPortal(
    <div
      className="fixed inset-0 bg-ink/50 flex items-center justify-center z-[9999] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="restore-draft-title"
    >
      <div className="bg-white rounded-xl border border-ink-line shadow-card-hover p-6 w-full max-w-md">
        <h2 id="restore-draft-title" className="text-lg font-medium text-ink">
          Unsaved draft found
        </h2>

        <p className="text-sm text-ink-body mt-2">
          {rowCount} {rowCount === 1 ? 'row was' : 'rows were'} left unsaved on {screenLabel}.
        </p>

        <p className="text-sm text-ink-body mt-1">
          Autosaved{' '}
          <span className="font-medium text-ink">{format(savedAt, 'd MMM yyyy, h:mm a')}</span>{' '}
          <span className="tabular-nums">({formatDistanceToNow(savedAt, { addSuffix: true })})</span>
        </p>

        <p className="text-xs text-ink-body mt-3">
          This is a local copy from this browser only — nothing was filed or saved to the client&apos;s
          records.
        </p>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" size="md" onClick={onDiscard}>
            Discard
          </Button>
          <Button variant="primary" size="md" onClick={onRestore}>
            Restore draft
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RestoreDraftModal;
